<script lang="ts" setup>
	import { computed, ref, watch } from 'vue';
	import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
	import { useModalStore } from '@/store/modal.store';
	import { useTeamStore } from '@/store/team.store';
	import { useRoute } from 'vue-router';
	import { useAuthStore } from '@/store/auth.store';
	import { IUser } from '@/types';
	import DefaultBtn from "@/components/block/DefaultBtn.vue";

  const props = defineProps<{
    closeSignal?: boolean
  }>();

  watch(() => props.closeSignal, () => {
    if (props.closeSignal) {
      close();
    }
  });

	const teamStore = useTeamStore();
	const modalStore = useModalStore();
	const authStore = useAuthStore();
	const route = useRoute();
	const visiblePassword = ref(false);
	const errorCMC = ref('');
	const password = ref('');
	const loading = ref(false);
	const loadingCode = ref(false);

	const userId = computed<string>(() => String(modalStore.modalPayload.id || route.params.userId || authStore.user.id || '-'));
	const originUser = computed<IUser | undefined>(() => teamStore.usersMap.get(userId.value));
	const userData = ref<IUser | undefined>(originUser.value && JSON.parse(JSON.stringify(originUser.value)) || undefined);

	watch(() => originUser.value, () => {
		userData.value = originUser.value && JSON.parse(JSON.stringify(originUser.value)) || undefined;
	});

	const avatarFile = ref<File | null>(null);
	const avatarPreviewUrl = ref<string | null>(null);

	watch(() => userData.value?.phone, (current, prev) => {
		if (current !== prev && userData.value?.phone) {
			const digitsOnly = userData.value?.phone.replace(/\D/g, '');
			userData.value.phone = ('+1' + digitsOnly.replace(/^1/, '')).slice(0, 12);
		}
	});

	async function handleSubmit() {
		loading.value = true;
    try {
      if (userData.value) {
        if (avatarFile.value) {
          await uploadAvatar();
        }

        if (authStore.user.id === userId.value) {

          if (userData.value.email !== originUser.value?.email) {
            teamStore.sendEditEmailCode(userId.value, userData.value.email)
              .then(() => {
                modalStore.openModal('ProfileEmailUpdated');
              });
          } else {
            teamStore.editUser(userId.value, userData.value)
              .then(() => {
                modalStore.openModal('ProfileInformationUpdated');
              });
          }

        } else {
          teamStore.editUser(userId.value, userData.value)
            .then(() => {
              modalStore.openModal('ProfileInformationUpdated');
            });
        }
        
        
      }
    } catch (error) {
      console.error('Error updating user data:', error);
    }
  }

	async function handleSubmitWithCode() {

    try {
			loadingCode.value = true;
      if (userData.value) {

        Promise.all([
          teamStore.editUser(userId.value, userData.value),
          teamStore.editUserEmail(userId.value, userData.value.email, password.value),
        ])
          .then(() => {
            modalStore.openModal('ProfileInformationUpdated');
          });
      }
    } catch (error) {
      console.error('Error updating user data:', error);
			loadingCode.value = false;
    }
  }

	const reader = new FileReader();
	function handleAvatarChange(event:any) {
		const file = event.target.files[0];
		if (file) {
			avatarFile.value = file;
			
			reader.onload = (e) => {
				avatarPreviewUrl.value = String(e.target?.result);
			};
			reader.readAsDataURL(file); 
		}
	}

	async function uploadAvatar() {

		if (originUser.value) {
			if (!avatarFile.value) {
				console.error('No file selected.');
				return;
			}

			const storage = getStorage();
			const storageRefAlias = storageRef(storage, `avatars/${originUser.value.id}/${avatarFile.value.name}`);

			try {
				if (userData.value) {
					await uploadBytes(storageRefAlias, avatarFile.value);
					const downloadURL = await getDownloadURL(storageRefAlias);
					userData.value.img = downloadURL;
				}
				
				console.log('Avatar uploaded successfully.');
			} catch (error) {
				console.error('Error uploading avatar:', error);
			}
		}
	}

	function areYouSure() {
		modalStore.openModal('ProfileInformationNoUpdated');
	}

	const togglePasswordVisibility = () => {
		visiblePassword.value = !visiblePassword.value;
	};

	const buttonText = ref('Send code again');
	const sendCode = () => {
		buttonText.value = 'New code was sent!';
		handleSubmit();
	};

</script>

<template>

	<div v-if="modalStore.isModalVisible('UserProfileEdit') && userData" class="modal modal--profile modal--active">
		<div class="modal__body login__enter" >
			<div class="modal__header">
				<p class="modal__subtitle">
					<span class="material-symbols-outlined">edit</span>
					<span>User Profile Edit</span>
				</p>
				<button @click="modalStore.closeModal" class="modal__close">
					<span class="material-symbols-outlined">close</span>
				</button>
			</div>
			<form @submit.prevent="handleSubmit">
				<div class="modal__main">

					<div class="modal__photo profile__info_photo">
						<div v-if="avatarPreviewUrl || userData.img" class="modal__photo_avatar">
							<img :src="avatarPreviewUrl || userData.img" alt="Avatar Preview">
						</div>
						<div v-else class="modal__photo_ico profile__info_icon">
							<img src="/img/user-icon__initials.svg" alt="Default Icon">
						</div>
						<div class="modal__placeholder profile__info_placeholder">
							<label for="avatar">
								<img src="/img/add_photo.svg" alt="Add Photo">
							</label>
							<input id="avatar" type="file" @change="handleAvatarChange" class="login__password">
						</div>
					</div>

					<div class="modal__main_wrap">
						<div class="login__item">
							<label for="userDataName">First Name*</label>
							<input v-model="userData.name" placeholder="First Name*" id="userDataName" type="text"
								class="login__password">
							<span class="material-symbols-outlined login__item_ico">person</span>
						</div>

						<div class="login__item">
							<label for="userDataMiddleName">Middle Name</label>
							<input v-model="userData.middleName" id="userDataMiddleName" placeholder="Middle Name" type="text"
								class="login__password">
							<span class="material-symbols-outlined login__item_ico">person</span>
						</div>

						<div class="login__item">
							<label for="userDataEmail">Email*</label>
							<input v-model="userData.email" placeholder="Email*" id="userDataEmail" type="email"
								class="login__password">
							<span class="material-symbols-outlined login__item_ico">Email</span>
						</div>

						<div class="login__item">
							<label for="userDataLastName">Last Name*</label>
							<input v-model="userData.lastName" placeholder="Last Name*" id="userDataLastName" type="text"
								class="login__password">
							<span class="material-symbols-outlined login__item_ico">person</span>
						</div>

						<div class="login__item">
							<label for="userDataPhone">Phone Number</label>
							<input v-model="userData.phone" placeholder="Phone Number" id="userDataPhone" type="phone"
								class="login__password">
							<span class="material-symbols-outlined login__item_ico">phone</span>
						</div>

					</div>

					<div class="modal__btn">
						<button class="btn btn--white" type="button" @click="areYouSure">Undo</button>
						<DefaultBtn class="btn" type="submit" :loading="loading" text="Save" />
					</div>
				</div>
			</form>
		</div>
	</div>

	<div v-if="modalStore.isModalVisible('ProfileInformationUpdated')" class="modal modal--profile modal--active">
		<div class="modal__body" @submit.prevent="handleSubmit">
			<div class="modal__header">
				<p class="modal__subtitle">
					<span class="material-symbols-outlined">edit</span>
					<span>User Profile Edit</span>
				</p>
				<button @click="modalStore.closeModal" class="modal__close">
					<span class="material-symbols-outlined">close</span>
				</button>
			</div>
			<div class="modal__main">
				<svg class="modal__update_svg" width="93" height="94" viewBox="0 0 93 94" fill="none"
					xmlns="http://www.w3.org/2000/svg">
					<path
						d="M39.5532 57.505L28.1823 46.1341C27.5046 45.4564 26.6527 45.1096 25.6267 45.094C24.6007 45.0782 23.7331 45.425 23.024 46.1341C22.315 46.8432 21.9604 47.7029 21.9604 48.7132C21.9604 49.7234 22.315 50.5831 23.024 51.2922L36.4563 64.7245C37.3411 65.6093 38.3734 66.0517 39.5532 66.0517C40.733 66.0517 41.7652 65.6093 42.65 64.7245L69.8817 37.4928C70.5595 36.8151 70.9062 35.9633 70.9219 34.9373C70.9376 33.9112 70.5909 33.0437 69.8817 32.3346C69.1726 31.6255 68.313 31.271 67.3027 31.271C66.2924 31.271 65.4327 31.6255 64.7236 32.3346L39.5532 57.505ZM46.5082 93.5C40.0768 93.5 34.0316 92.2796 28.3727 89.8387C22.7136 87.3979 17.791 84.0854 13.605 79.9012C9.4188 75.7169 6.10477 70.7965 3.66286 65.1399C1.22096 59.4835 0 53.4396 0 47.0082C0 40.5768 1.22042 34.5316 3.66127 28.8727C6.10212 23.2136 9.41464 18.291 13.5988 14.105C17.7831 9.91881 22.7035 6.60478 28.3601 4.16287C34.0165 1.72096 40.0604 0.5 46.4918 0.5C52.9232 0.5 58.9684 1.72043 64.6273 4.16128C70.2864 6.60213 75.209 9.91464 79.395 14.0988C83.5812 18.2831 86.8952 23.2035 89.3371 28.8601C91.779 34.5165 93 40.5604 93 46.9918C93 53.4232 91.7796 59.4683 89.3387 65.1273C86.8979 70.7864 83.5854 75.709 79.4012 79.895C75.2169 84.0812 70.2965 87.3952 64.6399 89.8371C58.9835 92.279 52.9396 93.5 46.5082 93.5ZM46.5 86.158C57.4316 86.158 66.6908 82.3646 74.2777 74.7777C81.8646 67.1908 85.658 57.9316 85.658 47C85.658 36.0684 81.8646 26.8092 74.2777 19.2223C66.6908 11.6354 57.4316 7.842 46.5 7.842C35.5684 7.842 26.3092 11.6354 18.7223 19.2223C11.1354 26.8092 7.342 36.0684 7.342 47C7.342 57.9316 11.1354 67.1908 18.7223 74.7777C26.3092 82.3646 35.5684 86.158 46.5 86.158Z"
						fill="#2563EB" />
				</svg>
				<p class="modal__update">Profile information updated!</p>
			</div>
		</div>
	</div>

	<div v-if="modalStore.isModalVisible('ProfileInformationNoUpdated')" class="modal modal--profile modal--active">
		<div class="modal__body" @submit.prevent="handleSubmit">
			<div class="modal__header">
				<p class="modal__subtitle">
					<span class="material-symbols-outlined">edit</span>
					<span>User Profile Edit</span>
				</p>
				<button @click="modalStore.closeModal" class="modal__close">
					<span class="material-symbols-outlined">close</span>
				</button>
			</div>
			<div class="modal__main">
				<svg class="modal__update_svg" width="100" height="100" viewBox="0 0 100 100" fill="none"
					xmlns="http://www.w3.org/2000/svg">
					<path
						d="M49.9429 79.0138C51.4181 79.0138 52.6642 78.5045 53.681 77.4859C54.6977 76.4673 55.206 75.2204 55.206 73.7451C55.206 72.2699 54.6967 71.0239 53.6781 70.0072C52.6595 68.9904 51.4126 68.482 49.9373 68.482C48.4621 68.482 47.2161 68.9913 46.1994 70.0099C45.1826 71.0285 44.6742 72.2754 44.6742 73.7507C44.6742 75.2259 45.1835 76.472 46.2021 77.4888C47.2207 78.5055 48.4676 79.0138 49.9429 79.0138ZM50.0084 99.0844C43.2197 99.0844 36.8387 97.7962 30.8653 95.2197C24.8919 92.6433 19.6959 89.1467 15.2773 84.7301C10.8585 80.3134 7.36041 75.1196 4.78285 69.1488C2.20529 63.1782 0.916504 56.7985 0.916504 50.0098C0.916504 43.2212 2.20473 36.8402 4.78117 30.8668C7.35762 24.8934 10.8542 19.6973 15.2708 15.2787C19.6875 10.86 24.8813 7.36188 30.852 4.78432C36.8227 2.20676 43.2024 0.917969 49.9911 0.917969C56.7797 0.917969 63.1607 2.2062 69.1341 4.78264C75.1075 7.35909 80.3036 10.8556 84.7222 15.2723C89.1409 19.689 92.639 24.8827 95.2166 30.8535C97.7941 36.8242 99.0829 43.2039 99.0829 49.9925C99.0829 56.7812 97.7947 63.1622 95.2183 69.1355C92.6418 75.109 89.1453 80.305 84.7286 84.7236C80.3119 89.1423 75.1182 92.6405 69.1474 95.218C63.1767 97.7956 56.797 99.0844 50.0084 99.0844ZM49.9997 91.3345C61.5386 91.3345 71.3122 87.3304 79.3205 79.322C87.3289 71.3137 91.3331 61.5401 91.3331 50.0012C91.3331 38.4623 87.3289 28.6887 79.3205 20.6803C71.3122 12.672 61.5386 8.66784 49.9997 8.66784C38.4608 8.66784 28.6872 12.672 20.6789 20.6803C12.6705 28.6887 8.66638 38.4623 8.66638 50.0012C8.66638 61.5401 12.6705 71.3137 20.6789 79.322C28.6872 87.3304 38.4608 91.3345 49.9997 91.3345ZM50.4182 27.8838C52.802 27.8838 54.865 28.6343 56.6071 30.1351C58.3492 31.6359 59.2203 33.5119 59.2203 35.7631C59.2203 37.6576 58.6639 39.3533 57.5511 40.8502C56.4382 42.3473 55.1665 43.7416 53.7357 45.0333C51.8602 46.6971 50.209 48.5272 48.7819 50.5238C47.355 52.5203 46.5918 54.744 46.4924 57.1949C46.4593 58.1354 46.7905 58.9236 47.486 59.5595C48.1815 60.1954 48.9929 60.5133 49.9203 60.5133C50.9138 60.5133 51.755 60.1821 52.4439 59.5197C53.1327 58.8574 53.5732 58.0459 53.7653 57.0855C54.1098 55.3103 54.8434 53.7288 55.9662 52.3412C57.0889 50.9534 58.3052 49.6338 59.615 48.3825C61.4979 46.5471 63.1415 44.5459 64.5457 42.3788C65.95 40.2116 66.6521 37.7945 66.6521 35.1272C66.6521 31.0337 65.0392 27.6704 61.8134 25.0374C58.5876 22.4044 54.8219 21.0879 50.5164 21.0879C47.4429 21.0879 44.545 21.7685 41.8226 23.1297C39.1002 24.4909 36.9805 26.4764 35.4636 29.0863C34.9933 29.8877 34.8423 30.7313 35.0107 31.617C35.179 32.5027 35.6336 33.1752 36.3743 33.6345C37.3171 34.1593 38.2853 34.3091 39.2789 34.0838C40.2724 33.8587 41.117 33.2924 41.8125 32.3849C42.8591 31.0269 44.1259 29.9373 45.613 29.116C47.1 28.2945 48.7018 27.8838 50.4182 27.8838Z"
						fill="#FF3F6D" />
				</svg>

				<p class="modal__update">Are you sure?</p>
				<p class="modal__update_text">You are about to close this page without saving.</p>
				<div class="modal__update_btn">
					<button class="btn btn--white btn--border" type="button" @click="modalStore.closeModal">Close</button>
					<button class="btn" type="submit" @click="modalStore.openModal('UserProfileEdit')">Continue</button>
				</div>
			</div>
		</div>
	</div>

	<div v-if="modalStore.isModalVisible('ProfileEmailUpdated')" class="modal modal--profile modal--active">
		<div class="modal__body">
			<div class="modal__header">
				<p class="modal__subtitle">
					<span class="material-symbols-outlined">edit</span>
					<span>User Profile Edit</span>
				</p>
				<button @click="modalStore.closeModal" class="modal__close">
					<span class="material-symbols-outlined">close</span>
				</button>
			</div>

				<div class="modal__main">
					<form @submit.prevent="handleSubmitWithCode">
					<p class="modal__update">Enter security code.</p>
					<p class="modal__subtitle">Your email address has been updated. Enter the code we sent to<br> your new
						address.</p>
					<div class="modal__main_wrap">
						<div class="login__item">
							<input autocomplete="one-time-code" v-model="password" :type="visiblePassword ? 'number' : 'password'"
								class="login__password" placeholder="Enter Code">

							<span class="material-symbols-outlined login__item_ico ico__code">pin</span>
							<button class="login__item_btn" type="button" @click="togglePasswordVisibility">
								<span class="material-symbols-outlined login__item_eyes eyes--off"
									:class="{ 'login__item_eyes--active': !visiblePassword }">visibility_off</span>
								<span class="material-symbols-outlined login__item_eyes eyes--visible"
									:class="{ 'login__item_eyes--active': visiblePassword }">visibility</span>
							</button>

							<p class="error" v-if="errorCMC">{{ errorCMC }}</p>
						</div>
					</div>
					<DefaultBtn class="btn btn__code" type="submit" :loading="loadingCode" text="Continue" />
					<button to="/"  class="login__reset"  @click.prevent="sendCode">{{ buttonText }}</button>
				</form>
				</div>
	
		</div>
	</div>

</template>

<style scoped>
	.btn--white{
		border: 1px solid #E2E8F0
	}

	.modal__photo_avatar img {
		border-radius: 50%;
	}
	.login__item_ico{
		top: 63%;
	}
	.modal__subtitle{
		text-align: center;
		font-size: 16px;
		line-height: 25px;
		margin-bottom: 32px;
	}
	.ico__code{
		top: 50%;
	}
	.btn__code{
		max-width: 333px;
		width: 333px;
		margin: 0 auto;
		box-shadow: none;
	}

	.login__reset{
		background: none;
		border: none;
		font-size: 16px;
		margin-top: 30px;
	}
	.btn{
		box-shadow: none;
	}

	form{
		display: flex;
		align-items: center;
		flex-direction: column;
	}

</style>